import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, BookOpen, Trophy, Clock, CheckCircle, MessageCircle, MapPin, Navigation, Phone, Mail, Bus } from 'lucide-react';
import AnimatedExamText from '../components/AnimatedExamText';
import FloatingCubes from '../components/FloatingCubes';
import SEO from '../components/SEO';
import { LocalBusinessSchema, BreadcrumbSchema } from '../components/StructuredData';
import { supabase } from '../lib/supabaseClient';

// Logo URLs
const cambridgeLogo = "/cambridge-logo.png";
const edexcelLogo = "/edexcel-logo.png";

// Hero carousel images — add your own to public/hero/
const HERO_IMAGES = [
  '/hero/hero-1.webp',
  '/hero/hero-2.webp',
  '/hero/hero-3.webp',
];

const TutorCard = ({ tutor }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => setIsHovered(!isHovered)}
    >
      {/* Tutor Image */}
      {tutor.image_url ? (
        <img
          src={tutor.image_url}
          alt={tutor.name}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          <span className="text-white/20 font-heading font-bold text-8xl">
            {tutor.name?.charAt(0)}
          </span>
        </div>
      )}

      {/* Always-visible bottom gradient with name */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-2 sm:p-5 transition-opacity duration-500">
        <div>
          <p className="text-white/70 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-0.5 sm:mb-1">
            {tutor.subjects?.join(' · ') || 'Tutor'}
          </p>
          <h3 className="text-white font-heading font-bold text-sm sm:text-lg leading-tight">
            {tutor.name}
          </h3>
        </div>
      </div>

      {/* Hover overlay with more info */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30 flex flex-col justify-end p-2 sm:p-5 transition-all duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
        <p className="text-aspire-red text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1 sm:mb-2">
          {tutor.subjects?.join(' · ') || 'Tutor'}
        </p>
        <h3 className="text-white font-heading font-bold text-sm sm:text-xl mb-1 sm:mb-2 leading-tight">
          {tutor.name}
        </h3>
        <p className="text-white/60 text-xs sm:text-sm mb-1 hidden sm:block">{tutor.qualification}</p>
        {tutor.experience && (
          <p className="text-white/40 text-xs hidden sm:block">{tutor.experience}</p>
        )}
      </div>
    </div>
  );
};

const HomePage = () => {
  const [subjects, setSubjects] = useState([]);
  const [results, setResults] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subjectsRes, resultsRes, tutorsRes] = await Promise.all([
          supabase.from('subjects').select('*').eq('level', 'IGCSE'),
          supabase.from('results').select('*').limit(6),
          supabase.from('tutors').select('*')
        ]);
        if (subjectsRes.data) setSubjects(subjectsRes.data.slice(0, 6));
        if (resultsRes.data) setResults(resultsRes.data);
        if (tutorsRes.data) setTutors(tutorsRes.data);
      } catch (e) {
        console.error('Error fetching data:', e);
      }
    };
    fetchData();
  }, []);

  const stats = [
    { icon: Users, value: '500+', label: 'Students Enrolled' },
    { icon: Award, value: '95%', label: 'A*/A Rate', highlight: true },
    { icon: BookOpen, value: '10+', label: 'Subjects Offered' },
    { icon: Trophy, value: '8+', label: 'Years Excellence' },
  ];

  // USP scroll-triggered entrance animation
  const uspRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.usp-card-enter');
            cards.forEach((card, i) => {
              setTimeout(() => card.classList.add('visible'), i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (uspRef.current) observer.observe(uspRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <main data-testid="home-page" className="relative overflow-hidden">
      <SEO path="/" />
      <LocalBusinessSchema />
      <BreadcrumbSchema items={[{ name: 'Home', href: '/' }]} />
      <FloatingCubes />

      
      {/* Hero Section */}
      <section className="relative bg-surface overflow-hidden" data-testid="hero-section">
        {/* Topography Background - behind cubes */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-25 z-0" 
          style={{backgroundImage: `url('/topography-bg.jpg')`}}
        ></div>
        <div className="absolute inset-0 bg-noise opacity-[0.02] z-0"></div>
        <div className="container-main py-16 md:py-24 lg:py-32 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 text-sm font-bold uppercase tracking-widest mb-6">
                <Clock className="w-4 h-4" />
                Limited Seats for 2026 Batch
              </div>
              

<AnimatedExamText />

              <p className="text-base sm:text-xl text-gray-600 mb-6 max-w-lg leading-relaxed font-medium">
                Sri Lanka's premier institution for IGCSE and A Level tuition. 
                Join hundreds of successful students who achieved their academic goals with Aspire Academy.
              </p>
              {/* Exam Board Logos */}
              <div className="flex items-center gap-3 sm:gap-6 mb-8">
                <img src={cambridgeLogo} alt="Cambridge" className="h-10 w-auto" />
                <img src={edexcelLogo} alt="Edexcel" className="h-16 w-auto" />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/igcse" 
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="hero-register-btn"
                >
                  Register Now
                  <ArrowRight className="w-5 h-5" />
                </Link>
                
              </div>
            </div>
            {/* Hero Carousel — stacked photo effect */}
            <div className="relative pr-3 pb-3 sm:pr-7 sm:pb-7">
              {/* Stack container — square to match 1080×1080 images */}
              <div
                className="relative"
                style={{ aspectRatio: '1 / 1', width: '100%' }}
                onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
                onTouchEnd={e => {
                  if (touchStartX.current === null) return;
                  const dx = e.changedTouches[0].clientX - touchStartX.current;
                  if (Math.abs(dx) > 40) {
                    setHeroIndex(i => dx < 0
                      ? (i + 1) % HERO_IMAGES.length
                      : (i - 1 + HERO_IMAGES.length) % HERO_IMAGES.length
                    );
                  }
                  touchStartX.current = null;
                }}
              >
                {HERO_IMAGES.map((src, imgIdx) => {
                  const total = HERO_IMAGES.length;
                  const diff = ((imgIdx - heroIndex) % total + total) % total;

                  let transform, zIndex, opacity, shadow;
                  if (diff === 0) {
                    transform = 'rotate(0deg) translate(0px, 0px)';
                    zIndex = 30; opacity = 1;
                    shadow = '0 20px 40px rgba(0,0,0,0.25)';
                  } else if (diff === 1) {
                    transform = 'rotate(4deg) translate(14px, 10px)';
                    zIndex = 20; opacity = 1;
                    shadow = '0 12px 28px rgba(0,0,0,0.18)';
                  } else if (diff === 2) {
                    transform = 'rotate(8deg) translate(28px, 20px)';
                    zIndex = 10; opacity = 0.9;
                    shadow = '0 8px 20px rgba(0,0,0,0.14)';
                  } else {
                    transform = 'rotate(10deg) translate(36px, 26px)';
                    zIndex = 0; opacity = 0;
                    shadow = 'none';
                  }

                  return (
                    <div
                      key={imgIdx}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        transform,
                        zIndex,
                        opacity,
                        boxShadow: shadow,
                        transition: 'transform 0.7s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={src}
                        alt={`Aspire Academy ${imgIdx + 1}`}
                        className="w-full h-full object-cover"
                        loading={imgIdx === 0 ? 'eager' : 'lazy'}
                        fetchPriority={imgIdx === 0 ? 'high' : 'low'}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Controls — arrows + dots */}
              <div className="flex items-center justify-center gap-4 mt-10">
                {/* Prev arrow */}
                <button
                  onClick={() => setHeroIndex(i => (i - 1 + HERO_IMAGES.length) % HERO_IMAGES.length)}
                  aria-label="Previous slide"
                  className="text-gray-400 hover:text-aspire-red transition-colors duration-200"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="11 4 6 9 11 14" />
                  </svg>
                </button>

                {/* Dots */}
                <div className="flex items-center gap-2">
                  {HERO_IMAGES.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setHeroIndex(i)}
                      aria-label={`Go to slide ${i + 1}`}
                      style={{ transition: 'all 0.3s ease' }}
                      className={`rounded-full ${
                        i === heroIndex
                          ? 'w-6 h-2 bg-aspire-red'
                          : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>

                {/* Next arrow */}
                <button
                  onClick={() => setHeroIndex(i => (i + 1) % HERO_IMAGES.length)}
                  aria-label="Next slide"
                  className="text-gray-400 hover:text-aspire-red transition-colors duration-200"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="7 4 12 9 7 14" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Infinite Scroll */}
      <section className="relative bg-charcoal py-12 overflow-hidden z-10" data-testid="stats-section">
        <div className="relative">
          {/* Scrolling container */}
          <div className="flex animate-scroll-slow">
            {/* First set */}
            {stats.map((stat, index) => (
              <div key={`stat-1-${index}`} className="flex-shrink-0 w-40 md:w-64 px-4 md:px-8 text-center">
                <stat.icon className={`w-8 md:w-10 h-8 md:h-10 ${stat.highlight ? 'text-amber-500' : 'text-aspire-red'} mx-auto mb-2 md:mb-3`} />
                <div className={`font-heading font-extrabold text-3xl md:text-5xl mb-1 ${stat.highlight ? 'text-amber-500' : 'text-white'}`}>{stat.value}</div>
                <div className="text-gray-400 text-sm md:text-base font-medium">{stat.label}</div>
              </div>
            ))}
            {/* Duplicate for seamless loop */}
            {stats.map((stat, index) => (
              <div key={`stat-2-${index}`} className="flex-shrink-0 w-40 md:w-64 px-4 md:px-8 text-center">
                <stat.icon className={`w-8 md:w-10 h-8 md:h-10 ${stat.highlight ? 'text-amber-500' : 'text-aspire-red'} mx-auto mb-2 md:mb-3`} />
                <div className={`font-heading font-extrabold text-3xl md:text-5xl mb-1 ${stat.highlight ? 'text-amber-500' : 'text-white'}`}>{stat.value}</div>
                <div className="text-gray-400 text-sm md:text-base font-medium">{stat.label}</div>
              </div>
            ))}
            {/* Third set for extra smoothness */}
            {stats.map((stat, index) => (
              <div key={`stat-3-${index}`} className="flex-shrink-0 w-40 md:w-64 px-4 md:px-8 text-center">
                <stat.icon className={`w-8 md:w-10 h-8 md:h-10 ${stat.highlight ? 'text-amber-500' : 'text-aspire-red'} mx-auto mb-2 md:mb-3`} />
                <div className={`font-heading font-extrabold text-3xl md:text-5xl mb-1 ${stat.highlight ? 'text-amber-500' : 'text-white'}`}>{stat.value}</div>
                <div className="text-gray-400 text-sm md:text-base font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section className="section-spacing bg-white" data-testid="programs-section">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="label-uppercase text-aspire-red">Our Programs</span>
            <h2 className="heading-section mt-2">Academic Excellence Awaits</h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg font-medium">
              Comprehensive tuition programs for Cambridge and Edexcel syllabi, 
              designed to help students achieve outstanding results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* IGCSE Card */}
            <div className="group border-l-4 border-aspire-red bg-white shadow-sm hover:shadow-lg transition-all p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-aspire-red/10 flex items-center justify-center">
                  <BookOpen className="w-7 h-7 text-aspire-red" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-2xl">IGCSE (O Levels)</h3>
                  <p className="text-base text-gray-500 font-medium">Cambridge & Edexcel</p>
                </div>
              </div>
              <p className="text-gray-600 mb-6 text-lg font-medium">
                Foundation courses preparing students for international GCSE examinations 
                across all major subjects.
              </p>
              <ul className="space-y-2 mb-6">
                {['Mathematics', 'Sciences', 'Business Studies', 'Languages'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-base text-gray-700 font-medium">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/igcse" 
                className="inline-flex items-center gap-2 text-aspire-red font-bold text-base hover:gap-3 transition-all"
                data-testid="igcse-explore-btn"
              >
                Explore IGCSE Programs
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* A Levels Card */}
            <div className="group border-l-4 border-aspire-red bg-white shadow-sm hover:shadow-lg transition-all p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-aspire-red/10 flex items-center justify-center">
                  <Award className="w-7 h-7 text-aspire-red" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-2xl">A Levels (AS & A2)</h3>
                  <p className="text-base text-gray-500 font-medium">Edexcel International</p>
                </div>
              </div>
              <p className="text-gray-600 mb-6 text-lg font-medium">
                Advanced level courses for university preparation, 
                with comprehensive coverage of AS and A2 syllabi.
              </p>
              <ul className="space-y-2 mb-6">
                {['Advanced Mathematics', 'Pure Sciences', 'Commerce Stream', 'Humanities'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-base text-gray-700 font-medium">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link 
                to="/a-levels" 
                className="inline-flex items-center gap-2 text-aspire-red font-bold text-base hover:gap-3 transition-all"
                data-testid="alevels-explore-btn"
              >
                Explore A Level Programs
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>



      {/* Why Choose Us — 4 Animated USP Cards */}
      <section className="section-spacing bg-white" data-testid="features-section">
        <div className="container-main">
          <div className="text-center mb-14">
            <span className="label-uppercase text-amber-500">Why Aspire Academy</span>
            <h2 className="heading-section mt-2 text-gray-900">A Track Record of Excellence</h2>
          </div>

          <div ref={uspRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Small Batch Sizes */}
            <div className="usp-card-enter group bg-white/[0.9] border border-white/[0.06] p-4 sm:p-8 text-center transition-all duration-500 hover:-translate-y-2 hover:bg-white/[0.08]">
              <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center relative">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  {/* 5 dots in a small circle formation */}
                  {[0, 1, 2, 3, 4].map((i) => {
                    const angle = (i * 72 - 90) * (Math.PI / 180);
                    const cx = 50 + 25 * Math.cos(angle);
                    const cy = 50 + 25 * Math.sin(angle);
                    return (
                      <circle
                        key={i}
                        cx={cx}
                        cy={cy}
                        r="8"
                        fill="#B91C1C"
                        opacity="0.5"
                        style={{
                          animation: `usp-dot-pulse 2.5s ease-in-out ${i * 0.4}s infinite`,
                          transformOrigin: `${cx}px ${cy}px`
                        }}
                      />
                    );
                  })}
                  {/* Center dot — the "tutor" */}
                  <circle cx="50" cy="50" r="6" fill="#F59E0B" opacity="0.9" />
                </svg>
              </div>
              <h4 className="font-heading font-bold text-xl text-charcoal mb-3">Small Batch Sizes</h4>
              <p className="text-gray-400 text-sm leading-relaxed">Maximum 8 students per class ensuring personalized attention and interactive learning.</p>
            </div>

            {/* Expert Tutors */}
            <div className="usp-card-enter group bg-white/[0.04] border border-white/[0.06] p-4 sm:p-8 text-center transition-all duration-500 hover:-translate-y-2 hover:bg-white/[0.08]">
              <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                <svg viewBox="0 0 100 100" className="w-full h-full animate-usp-float">
                  {/* Graduation cap */}
                  <polygon points="50,20 15,40 50,55 85,40" fill="#B91C1C" />
                  <polygon points="50,55 15,40 15,42 50,57" fill="#991B1B" />
                  <polygon points="50,55 85,40 85,42 50,57" fill="#7F1D1D" />
                  {/* Tassel line */}
                  <line x1="78" y1="40" x2="78" y2="62" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="78" cy="65" r="3" fill="#F59E0B" />
                  {/* Book base */}
                  <rect x="30" y="62" width="40" height="6" rx="1" fill="gray-900" opacity="0.3" />
                  <rect x="33" y="68" width="34" height="5" rx="1" fill="gray-900" opacity="0.2" />
                </svg>
              </div>
              <h4 className="font-heading font-bold text-xl text-charcoal mb-3">Expert Tutors</h4>
              <p className="text-gray-400 text-sm leading-relaxed">Learn from qualified educators with years of Cambridge & Edexcel teaching experience.</p>
            </div>

            {/* Hybrid Classes */}
            <div className="usp-card-enter group bg-white/[0.04] border border-white/[0.06] p-4 sm:p-8 text-center transition-all duration-500 hover:-translate-y-2 hover:bg-white/[0.08]">
              <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center relative overflow-hidden">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  {/* Laptop icon (left) */}
                  <rect x="8" y="35" width="28" height="20" rx="2" fill="none" stroke="#B91C1C" strokeWidth="2.5" />
                  <rect x="12" y="39" width="20" height="12" rx="1" fill="#B91C1C" opacity="0.3" />
                  <rect x="4" y="55" width="36" height="3" rx="1.5" fill="#B91C1C" />
                  {/* Building icon (right) */}
                  <rect x="64" y="30" width="26" height="28" rx="1" fill="none" stroke="black" strokeWidth="2" opacity="0.7" />
                  <rect x="68" y="35" width="5" height="5" rx="0.5" fill="gray-900" opacity="0.4" />
                  <rect x="81" y="35" width="5" height="5" rx="0.5" fill="gray-900" opacity="0.4" />
                  <rect x="68" y="44" width="5" height="5" rx="0.5" fill="gray-900" opacity="0.4" />
                  <rect x="81" y="44" width="5" height="5" rx="0.5" fill="gray-900" opacity="0.4" />
                  <rect x="73" y="50" width="8" height="8" rx="0.5" fill="gray-900" opacity="0.3" />
                  <rect x="64" y="58" width="26" height="3" rx="1" fill="gray-900" opacity="0.5" />
                  {/* Connecting pulse dots */}
                  <circle r="3" fill="#F59E0B" style={{ animation: 'usp-pulse-dot 2s ease-in-out infinite' }}>
                    <animate attributeName="cx" from="40" to="60" dur="2s" repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0;1;1;0" dur="2s" repeatCount="indefinite" />
                    <animate attributeName="cy" values="48;48;48;48" dur="2s" repeatCount="indefinite" />
                  </circle>
                  {/* Dashed connection line */}
                  <line x1="40" y1="48" x2="62" y2="48" stroke="black" strokeWidth="1" strokeDasharray="3 3" opacity="0.2" />
                </svg>
              </div>
              <h4 className="font-heading font-bold text-xl text-charcoal mb-3">Hybrid Classes</h4>
              <p className="text-gray-400 text-sm leading-relaxed">Choose between in-person and online sessions, or switch between both seamlessly.</p>
            </div>

            {/* Class Recordings */}
            <div className="usp-card-enter group bg-white/[0.04] border border-white/[0.06] p-4 sm:p-8 text-center transition-all duration-500 hover:-translate-y-2 hover:bg-white/[0.08]">
              <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  {/* Ripple rings */}
                  <circle cx="50" cy="50" r="20" fill="none" stroke="#B91C1C" strokeWidth="1.5" opacity="0.3"
                    style={{ animation: 'usp-ripple 2.5s ease-out 0s infinite', transformOrigin: '50px 50px' }} />
                  <circle cx="50" cy="50" r="20" fill="none" stroke="#B91C1C" strokeWidth="1.5" opacity="0.3"
                    style={{ animation: 'usp-ripple 2.5s ease-out 0.8s infinite', transformOrigin: '50px 50px' }} />
                  <circle cx="50" cy="50" r="20" fill="none" stroke="#B91C1C" strokeWidth="1.5" opacity="0.3"
                    style={{ animation: 'usp-ripple 2.5s ease-out 1.6s infinite', transformOrigin: '50px 50px' }} />
                  {/* Play button */}
                  <circle cx="50" cy="50" r="18" fill="#B91C1C" />
                  <polygon points="44,38 44,62 66,50" fill="white" />
                  {/* REC dot */}
                  <circle cx="75" cy="25" r="4" fill="#EF4444">
                    <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <text x="82" y="28" fill="black" fontSize="9" fontWeight="bold" opacity="0.6">REC</text>
                </svg>
              </div>
              <h4 className="font-heading font-bold text-xl text-charcoal mb-3">Class Recordings</h4>
              <p className="text-gray-400 text-sm leading-relaxed">Never miss a lesson. All classes are recorded and available for revision anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Our Tutors */}
      {tutors.filter(t => t.image_url).length > 0 && (
        <section className="section-spacing bg-charcoal" data-testid="tutors-section">
          <div className="container-main">
            <div className="text-center mb-12">
              <span className="label-uppercase text-amber-500">Our Educators</span>
              <h2 className="heading-section mt-2 text-white">Meet Our Tutors</h2>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
                Learn from passionate educators with proven track records in Cambridge and Edexcel examinations.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-4 md:gap-5">
              {tutors.filter(t => t.image_url).map((tutor) => (
                <TutorCard key={tutor.tutor_id} tutor={tutor} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Results Preview */}
      {results.length > 0 && (
        <section className="section-spacing bg-charcoal z-10" data-testid="results-preview">
          <div className="container-main z-10">
            <div className="text-center mb-12">
              <span className="label-uppercase text-amber-500">Our Results</span>
              <h2 className="heading-section mt-2 text-white">Proven Success Stories</h2>
              <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
                Our students consistently achieve exceptional grades in Cambridge and Edexcel examinations.
              </p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 z-10">
              {results.map((result, index) => (
                <div
                  key={result.result_id}
                  className="bg-white/5 backdrop-blur p-2 sm:p-4 text-center"
                >
                  <div className="w-10 h-10 sm:w-16 sm:h-16 bg-aspire-red mx-auto mb-2 sm:mb-3 flex items-center justify-center">
                    <span className="font-heading font-bold text-base sm:text-2xl text-white">{result.grade}</span>
                  </div>
                  <h4 className="font-heading font-bold text-white text-xs sm:text-sm mb-0.5 sm:mb-1 line-clamp-2">{result.subject}</h4>
                  <p className="text-[10px] sm:text-xs text-gray-400">{result.level} • {result.year}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link 
                to="/results" 
                className="btn-primary inline-flex items-center gap-2"
                data-testid="view-all-results-btn"
              >
                View All Results
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Location Section — Prime Colombo 7 USP */}
      <section className="section-spacing bg-white" data-testid="location-section">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-stretch">

            {/* Left — Info */}
            <div className="flex flex-col justify-center">
              <span className="label-uppercase text-aspire-red mb-3">Our Location</span>
              <h2 className="heading-section mb-4">
                Situated in the Heart of <span className="text-aspire-red">Colombo 07</span>
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Aspire Academy is located on Srimath Anagarika Dharmapala Mawatha — 
                one of Colombo's most accessible and prestigious addresses. 
                Easy to reach from across the city by bus, tuk-tuk, or car.
              </p>

              {/* Address card */}
              <div className="border-l-4 border-aspire-red bg-gray-50 p-6 mb-6">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-aspire-red mt-0.5 shrink-0" />
                  <div>
                    <p className="font-bold text-charcoal">102/5A, Srimath Anagarika Dharmapala Mawatha</p>
                    <p className="text-gray-600">Colombo 00700, Sri Lanka</p>
                  </div>
                </div>
              </div>

              {/* Details grid */}
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-aspire-red mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Hours</p>
                    <p className="text-sm font-medium text-charcoal">Mon – Sat: 9:00 AM – 7:30 PM</p>
                    <p className="text-sm text-gray-500">Sunday: 9:00 AM – 5:00 PM</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Bus className="w-5 h-5 text-aspire-red mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Getting Here</p>
                    <p className="text-sm font-medium text-charcoal">Multiple bus routes nearby</p>
                    <p className="text-sm text-gray-500">Ample parking available</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-aspire-red mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">Call Us</p>
                    <a href="tel:+94743652899" className="text-sm font-medium text-charcoal hover:text-aspire-red transition-colors">+94 74 365 2899</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-aspire-red mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">WhatsApp</p>
                  <a href="https://wa.me/94743652899" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-charcoal hover:text-aspire-red transition-colors">Message us directly</a>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://maps.app.goo.gl/CJRai4y4rcQc9Jbw5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="get-directions-btn"
                >
                  <Navigation className="w-5 h-5" />
                  Get Directions
                </a>
                <Link to="/contact" className="btn-secondary inline-flex items-center justify-center gap-2">
                  Contact Us
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>

            {/* Right — Embedded Google Map */}
            <div className="relative min-h-[250px] sm:min-h-[320px] lg:min-h-0">
              <div className="absolute -top-4 -right-4 w-full h-full bg-aspire-red hidden lg:block"></div>
              <div className="relative z-10 w-full h-full min-h-[250px] sm:min-h-[320px] lg:min-h-[420px] border-2 border-gray-200 overflow-hidden shadow-lg">
                <iframe
                  title="Aspire Academy Location"
                  src="https://maps.google.com/maps?q=Aspire+Academy,+102/5A+Dharmapala+Mawatha,+Colombo+07,+Sri+Lanka&t=&z=17&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '420px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-aspire-red" data-testid="cta-section">
        <div className="container-main">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-4">
                Ready to Excel in Your Examinations?
              </h2>
              <p className="text-white/80 text-base sm:text-lg max-w-xl">
                Join Aspire Academy today and take the first step towards academic excellence.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/register" 
                className="bg-white text-aspire-red px-8 py-4 font-bold uppercase tracking-widest hover:bg-gray-100 transition-colors text-center"
                data-testid="cta-register-btn"
              >
                Register Now
              </Link>
              <a 
                href="https://wa.me/94743652899"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
